import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { Toast, ToastDescription, useToast } from "../../ui/toast";

type IToastProps = Parameters<ReturnType<typeof useToast>["show"]>[0];

const ToastRoot = forwardRef(function ToastRoot(_, ref) {
  const toast = useToast();
  const show = useCallback(
    (props: IToastProps) => {
      return toast.show({
        ...props,
        id: props.id ?? String(Math.random()),
      });
    },
    [toast],
  );
  useImperativeHandle(
    ref,
    useCallback(
      () => ({
        show,
        close: toast.close,
        closeAll: toast.closeAll,
        isActive: toast.isActive,
      }),
      [toast, show],
    ),
  );
  return <></>;
});

type ToastRef = {
  show: (props: Omit<IToastProps, "id">) => string;
  close: (id: string) => void;
  closeAll: () => void;
  isActive: (id: string) => boolean;
};

type ToastRefObj = {
  current: ToastRef | null;
};

let refs: ToastRefObj[] = [];

/**
 * Adds a ref to the end of the array,
 * which will be used to show the toasts until its ref becomes null.
 *
 * @param newRef the new ref, which must be stable for the life of the Toast instance.
 */
function addNewRef(newRef: ToastRef) {
  refs.push({ current: newRef });
}

/**
 * Removes the passed in ref from the file-level refs array using a strict equality check.
 *
 * @param oldRef the exact ref object to remove from the refs array.
 */
function removeOldRef(oldRef: ToastRef | null) {
  refs = refs.filter((r) => r.current !== oldRef);
}

export default function MontuToastProvider() {
  const modalRef = useRef<ToastRef | null>(null);

  /*
    This must use `useCallback` to ensure the ref doesn't get set
    to null and then a new ref every render.
    Failure to do so will cause whichever Toast *renders or re-renders*
    last to be the instance that is used,
    rather than being the Toast that was *mounted* last.
  */
  const setRef = useCallback((ref: ToastRef | null) => {
    // Since we know there's a ref, we'll update `refs` to use it.
    if (ref) {
      // store the ref in this toast instance to be able to remove it
      // from the array later when the ref becomes null.
      modalRef.current = ref;
      addNewRef(ref);
    } else {
      // remove the this toast's ref, wherever it is in the array.
      removeOldRef(modalRef.current);
    }
  }, []);

  return <ToastRoot ref={setRef} />;
}

function getRef() {
  const reversePriority = [...refs].reverse();
  const activeRef = reversePriority.find((ref) => ref?.current !== null);
  if (!activeRef) {
    return null;
  }
  return activeRef.current;
}

MontuToastProvider.show = (params: IToastProps) => {
  return getRef()?.show(params);
};

MontuToastProvider.close = (id: string) => {
  getRef()?.close(id);
};

MontuToastProvider.closeAll = () => {
  getRef()?.closeAll();
};

MontuToastProvider.isActive = (id: string) => {
  return getRef()?.isActive(id);
};

/** A bottom error toast with `message` as its text. */
export const showErrorToast = (message: string) =>
  MontuToastProvider.show({
    placement: "bottom",
    duration: 5000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription>{message}</ToastDescription>
      </Toast>
    ),
  });
